import * as babylon from "@babylonjs/core";

import {
  clampToLimits,
  evaluateJointValues,
  type Joint,
} from "@/shared/state/visualization/schema/v1/three-d";

import type * as components from "../components";
import { BabylonState } from "../state";
import {
  sampleAt,
  interpolationOf,
  type Interpolation,
} from "../systems/render/setPositionsToTime";

/**
 * Reused across the whole pass, so a 60fps loop over every posed joint allocates
 * nothing. Safe as module state because this runs to completion synchronously on
 * one thread, and the value never outlives a single iteration.
 */
const SCRATCH_AXIS = babylon.Vector3.Zero();

/** The same, for the model pass, so the two never share an intermediate. */
const MODEL_AXIS = babylon.Vector3.Zero();

/**
 * Poses every joint the panel drives, of either kind.
 *
 * Two mechanisms sit here because both write a joint value over a transform some
 * declaration produced, and both must land before `scene.render`:
 *
 * - {@link poseModelJoints} drives the joints a `model` component declares,
 *   from that model's `jointValues` sampled at the visualized time, overridden
 *   per joint by any live value `onSetModelJointPose` has written. This is the
 *   path an articulated model in the panel takes.
 * - {@link poseEntityJoints} applies a `JointPose` — a live value written
 *   imperatively from outside the ECS onto one entity, which is how a glTF
 *   exploded into per-node entities is posed.
 */
export function poseJoints(state: BabylonState) {
  poseModelJoints(state);
  poseEntityJoints(state);
}

/**
 * Applies each entity's live joint value over the transform its spec declares.
 *
 * Runs between `updateRotations`, which writes the orientation sampled from the
 * `Rotation` cache at the visualized time, and `resolveGlobalPositions`, which
 * composes the hierarchy — so a joint driven from the outside carries its whole
 * descendant subtree exactly as a topic-driven one does. Sitting after
 * `updateRotations` is what makes it an override rather than a suggestion:
 * anything the spec produced this frame has already been written by then.
 *
 * ## The two motions
 *
 * A `rotate` — URDF's `revolute` and `continuous` — turns the link about the
 * axis, and **overrides** the orientation the spec produced. A drivable joint's
 * spec is its rest pose, `{type: "axis_angle", axis, angle: 0}`, which is the
 * identity rotation, so for the case this exists to serve override and compose
 * agree. Override is the defined behaviour because the alternative is ambiguous:
 * once that spec's `angle` is bound to a topic, composing would add the driven
 * value on top of the recorded one and show a joint at neither position.
 *
 * A `slide` — `prismatic` — moves the link along the axis, and **adds to** the
 * offset the spec declares rather than replacing it. The two differ because the
 * rest values differ: a revolute link's rest rotation is identity, but a
 * prismatic link may sit anywhere in its parent's frame at `q = 0`, and its
 * travel is measured from wherever that is. The displacement is written here and
 * added by `resolveGlobalPositions` while it composes the chain — writing it
 * onto the `Position` component instead is not available, because
 * `setPositionsToTime` hands out `cache.allPoints` elements by reference, so
 * that would rewrite recorded history.
 *
 * Neither motion touches the other's channel: a revolute joint never translates
 * and a prismatic one never rotates, per the `ROBOTO_device` extension.
 *
 * An entity with a `rotate` pose but no `Rotation` component is left unturned.
 * The composition reads orientation off `Rotation`, so there is nowhere to put
 * the result; anything driving a joint this way has to declare a rest rotation
 * on the entity for that reason. A `slide` has no such requirement — its
 * displacement lives on the pose itself.
 *
 * **Nothing in the app drives a joint this way any more.** The path existed for
 * a glTF exploded into one entity per node, and a model is now drawn as a single
 * `model` component posed by {@link poseModelJoints} — see
 * `onSetModelJointPose`. What remains is the general mechanism: an entity
 * hierarchy assembled by hand can still be driven through it.
 */
function poseEntityJoints(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    const pose = entity.jointPose;
    if (!pose) {
      continue;
    }

    // A zero axis names no direction, so no value along or about it means
    // anything. Both branches below would otherwise divide by the axis length,
    // and a NaN here propagates into every descendant's transform through the
    // composition.
    const degenerate = pose.axis.lengthSquared() === 0;

    if (pose.motion === "slide") {
      if (degenerate) {
        pose.displacement.setAll(0);
      } else {
        pose.axis.normalizeToRef(SCRATCH_AXIS);
        SCRATCH_AXIS.scaleToRef(pose.value, pose.displacement);
      }
      continue;
    }

    pose.displacement.setAll(0);
    if (degenerate) {
      pose.quaternion.copyFromFloats(0, 0, 0, 1);
    } else {
      babylon.Quaternion.RotationAxisToRef(
        pose.axis,
        pose.value,
        pose.quaternion,
      );
    }
    if (entity.rotation) {
      // By reference rather than by copy. `updateRotations` replaces this field
      // with a freshly allocated quaternion whenever it runs, and nothing
      // downstream writes through it — `resolveGlobalPositions` multiplies it
      // into the global orientation, reading only.
      entity.rotation.current.quaternion = pose.quaternion;
    }
  }
}

/**
 * Poses the joints of every model in the scene, at the visualized time.
 *
 * ## What a joint value means
 *
 * The `ROBOTO_device` layout puts the fixed offset between two links on the
 * **joint's own node** and leaves the **child link's node** at identity, so the
 * link node is the joint's moving frame and nothing else. That is why the value
 * is written absolutely rather than composed onto whatever the node already
 * carries: at `q = 0` an absolute write reproduces the file exactly, which is the
 * property the migration rests on — posing every joint to zero must leave the
 * model standing precisely where the document describes.
 *
 * The two motions never touch each other's channel, per the extension: a
 * `revolute` or `continuous` joint only rotates, and a `prismatic` one only
 * translates by `normalize(axis) · q`. The axis is normalized on read because a
 * file need not supply a unit vector, and for a sliding joint that is not
 * cosmetic — an axis of `[0,0,2]` would otherwise send the link twice as far per
 * unit of travel.
 *
 * ## Where this sits, and why it is free to sit there
 *
 * Everything written here is *below* the model's anchor, in the model's own
 * frame. `resolveGlobalPositions` composes only the entity hierarchy and never
 * sees these nodes, and `applyRenderOrigin` translates only the anchor —
 * translating an ancestor cannot disturb a descendant's local transform. So the
 * one real constraint is "before `scene.render`"; it runs beside the other
 * sample-to-current-time systems because that is what it is.
 *
 * A joint the model excluded as unresolvable — part of a coupling cycle, or
 * following a driver the file does not contain — is absent from `jointOrder` and
 * is therefore never written, leaving its link at the zero pose the file was
 * exported in. See `mimicOrder`.
 *
 * Where the values come from, and which wins, is {@link drivenValues}.
 */
function poseModelJoints(state: BabylonState) {
  // Falls back to the epoch rather than returning: a model whose joints are all
  // constants still has a pose to take up, and a mimic with a non-zero offset is
  // not at its file transform even with its driver at zero.
  const time = state.sceneConfig.time ?? 0n;
  const interpolation = interpolationOf(state);

  for (const entity of Object.values(state.entities)) {
    const mesh = entity.mesh;
    const model = mesh?.model;
    if (!mesh || !model || model.nodesByName.size === 0) {
      continue;
    }

    const values = evaluateJointValues(
      model.jointOrder.order,
      drivenValues(mesh, model.jointOrder.order, time, interpolation),
    );

    for (const joint of model.jointOrder.order) {
      const node = model.nodesByName.get(joint.node);
      if (!node || !joint.axis) {
        continue;
      }
      babylon.Vector3.FromArrayToRef(joint.axis, 0, MODEL_AXIS);
      if (MODEL_AXIS.lengthSquared() === 0) {
        // A zero axis names no direction, so no value along or about it means
        // anything — and normalizing it would put a NaN into the node's
        // transform, which the whole subtree below would then inherit.
        continue;
      }
      MODEL_AXIS.normalize();

      if (joint.type === "prismatic") {
        MODEL_AXIS.scaleToRef(values[joint.name], node.position);
        continue;
      }
      // Assigned before it is written into: Babylon composes from the euler
      // `rotation` field while `rotationQuaternion` is null, which is the state
      // the glTF loader leaves a node with no rotation of its own in.
      node.rotationQuaternion ??= babylon.Quaternion.Identity();
      babylon.Quaternion.RotationAxisToRef(
        MODEL_AXIS,
        values[joint.name],
        node.rotationQuaternion,
      );
    }
  }
}

/**
 * What each independently driven joint of this model is at, right now.
 *
 * Two sources, in precedence order. A joint's **binding** is sampled from its
 * cache at the visualized time, blended or held between samples as
 * `interpolation` says; only the joints with a loaded cache appear.
 * Everything else is absent, which {@link evaluateJointValues} reads as zero —
 * the pose the file was exported in — so an unbound model needs no seeding and a
 * half-loaded one draws the joints it has rather than nothing.
 *
 * A joint's **live pose** then overrides that binding, exactly as a `JointPose`
 * overrides the orientation an entity's `Rotation` spec produced. Both are the
 * same distinction: the binding is a declaration, resolved on a poll, and the
 * pose is a value someone is holding the joint at this instant. Per joint rather
 * than all-or-nothing, so posing one joint of a bound model leaves the others
 * playing back their topics.
 *
 * Coupled joints are unaffected by either source. `evaluateJointValues` computes
 * a `mimic` from its driver and never reads `driven` for it, so a value written
 * against one is inert rather than half-applied.
 *
 * ## An authored value is held inside the joint's limits; a recorded one is not
 *
 * This is the one place that can tell the two apart, which is why the rule lives
 * here rather than in `evaluateJointValues`: by the time a value is a number in
 * `driven` it has lost the only thing that decides whether it may leave the
 * joint's declared travel.
 *
 * A value someone **authored** is a request to put the joint somewhere — a live
 * pose from a slider, or a constant typed into the joint's field, which reaches
 * this as a one-sample cache over a numeric spec. A robot cannot be posed past
 * its own stops, so the request is honoured as far as the joint can go and no
 * further.
 *
 * A value **recorded into a topic** is not a request, it is an observation, and
 * an observation outside the declared range is a finding rather than an error to
 * absorb: a real arm's joint sitting 2% past the upper limit its URDF states is
 * a discrepancy between the robot and its description, and it is only visible
 * while the panel keeps drawing what the data says. The same pass-through is
 * what makes a unit mistake announce itself — degrees fed into a joint read as
 * radians spins the link visibly rather than parking it against a stop, where it
 * would look like any joint at rest.
 */
function drivenValues(
  mesh: components.Mesh,
  joints: readonly Joint[],
  time: bigint,
  interpolation: Interpolation,
): Record<string, number> {
  const driven: Record<string, number> = {};
  const byName = new Map(joints.map((joint) => [joint.name, joint]));
  /** Held inside the joint's travel, for a joint this model actually declares. */
  const authored = (name: string, value: number): number => {
    const joint = byName.get(name);
    return joint === undefined ? value : clampToLimits(value, joint);
  };

  for (const [name, cache] of Object.entries(mesh.jointCaches ?? {})) {
    if (cache.load.state !== "loaded") {
      continue;
    }
    const value = jointValueAt(cache, time, interpolation);
    if (value === undefined) {
      continue;
    }
    // The spec, not the sample: a constant is a one-sample timeline by the time
    // it reaches the cache, and indistinguishable from recorded data here
    // without asking what was bound.
    driven[name] =
      typeof cache.spec.value === "number" ? authored(name, value) : value;
  }
  for (const [name, value] of Object.entries(mesh.jointPoses ?? {})) {
    driven[name] = authored(name, value);
  }
  return driven;
}

/**
 * The cached joint value at `time`: interpolated between the bracketing samples
 * when `interpolation` blends that pair (see `interpolatesBetween`), and
 * otherwise the latest sample at or before `time`.
 *
 * Linear, and clamped outside the cache's range in either mode, the way
 * `localPositionAt` is: before the first sample yields the first value, after
 * the last yields the last. A joint value is a scalar on a line, so there is no
 * counterpart to the rotation cache's slerp — lerping is exactly right here.
 *
 * Exported for its own test: the bracketing arithmetic is the part that goes
 * wrong, and it is invisible from the outside once a node has been posed.
 */
export function jointValueAt(
  cache: Pick<components.JointValueCache, "logtimes" | "values">,
  time: bigint,
  interpolation: Interpolation,
): number | undefined {
  const bracket = sampleAt(cache.logtimes, time, interpolation);
  if (bracket === undefined) {
    return undefined;
  }
  const lastValue = cache.values.at(bracket.index);
  if (lastValue === undefined) {
    return undefined;
  }
  const nextValue = cache.values.at(bracket.index + 1);
  if (bracket.fraction === undefined || nextValue === undefined) {
    return lastValue;
  }
  return lastValue + (nextValue - lastValue) * bracket.fraction;
}
